/* stylelint-disable */
import {airBlue, white, semiDarkGray, lightGray} from '@modifi/colors'
import {ButtonLink, Container, media} from '@modifi/ui'
import styled, {css, FlattenSimpleInterpolation} from 'styled-components'

export const LogoWidth = 200
export const LogoHeight = 28

type NavToggleProps = {
  isTransparent: boolean
  isOpen: boolean
  isHamburger: boolean
}

export const NavToggle = styled(ButtonLink)<NavToggleProps>`
  display: block;
  height: 44px;
  width: 44px;
  padding: 10px;
  background: transparent;
  min-width: auto;

  &:hover,
  &:focus,
  &:active {
    background: transparent;
  }

  > div {
    display: flex;
    height: 100%;
    width: 100%;
    flex-direction: column;
    justify-content: space-around;

    > span {
      height: 2px;
      width: 24px;
      background-color: ${({isTransparent}): string => (isTransparent ? white : airBlue)};
      transition: opacity 0.25s ease, transform 0.25s ease;
      transform-origin: 1px 50%;
      ${({isOpen}): FlattenSimpleInterpolation | string =>
        isOpen
          ? css`
              &:first-child {
                transform: translateX(3px) rotate(45deg);
              }
              &:nth-child(2) {
                opacity: 0;
              }
              &:last-child {
                transform: translateX(3px) rotate(-45deg);
              }
            `
          : ''}
    }
  }
`

export const MainNav = styled.nav`
  display: flex;
  justify-content: center;
  transition: opacity 0.4s, height 0.3s;
  flex-grow: 1;
`

export const PrimaryNavList = styled.ul`
  display: flex;
  justify-content: space-between;
  list-style: none;
  padding: 0;
  margin: 0;
  flex-direction: row;
  align-items: center;
  width: 100%;

  ul {
    position: absolute;
    box-shadow: 3px 4px 16px rgba(23, 61, 108, 0.12);
    border-radius: 6px;
  }

  > li {
    width: auto;
  }
`

export const NavItem = styled.li`
  color: ${semiDarkGray};
  white-space: nowrap;

  a {
    text-decoration: none;
    color: inherit;
  }

  .primary-nav-label::before {
    display: block;
    content: attr(title);
    font-weight: bold;
    height: 0;
    overflow: hidden;
    visibility: hidden;
  }

  &:hover .primary-nav-label {
    font-weight: 600;
  }
`

type DividerProps = {
  ml?: number
  mr?: number
  height: string
}

export const Divider = styled.li<DividerProps>`
  height: ${({height}): string => height};
  width: 1px !important;
  background-color: ${lightGray};
  margin-left: ${({ml}): FlattenSimpleInterpolation | string =>
    ml
      ? css`
          ${ml}px
        `
      : 'auto'};
  margin-right: ${({mr}): FlattenSimpleInterpolation | string =>
    mr
      ? css`
          ${mr}px
        `
      : ''};
`

export const NavigationWrapper = styled(Container)`
  display: flex;
  padding-left: 0;
  padding-right: 0;

  transition: min-height 0.3s, background-color 0.4s;

  width: 100%;
  align-items: center;

  .is-transparent & {
    background-color: transparent;
  }

  .is-transparent.is-not-open & {
    nav > ul:first-child > li,
    nav > ul:first-child > li > a,
    nav > ul:first-child > div > li {
      color: white;
    }
  }
`

export const LogoWrapper = styled.div`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: auto;
`

const navWithoutLogin = css`
  ${PrimaryNavList} > li:nth-last-child(2) {
    margin-left: auto;
  }
`

const navWithLogin = css`
  ${PrimaryNavList} > li:nth-last-child(4) {
    margin-left: auto;
  }
`

export const DesktopNavigationWrapper = styled(NavigationWrapper)`
  flex-direction: row;
  justify-content: space-between;
  padding: 0 40px;

  ${({isLoginHidden}: {isLoginHidden: boolean}): any =>
    isLoginHidden ? navWithoutLogin : navWithLogin}
`

export const MobileNavigationWrapper = styled(NavigationWrapper)`
  flex-direction: column;
  justify-content: flex-start;
  background-color: ${white};
  color: ${semiDarkGray};
  position: absolute;
  top: -999px;
  opacity: 0;
  border-radius: 6px;
  overflow: hidden;
  min-height: 0;
  align-items: stretch;
  margin-top: 16px;
  ${media.xs`
    margin: 16px;
    width: calc(100% - 32px);
  `}

  .is-open &,
  .is-nav-opening & {
    background-color: ${white};
    min-height: calc(100vh - 32px);
    padding-bottom: 32px;
  }

  ${MainNav} {
    flex-direction: column;
    height: 0;
    opacity: 0;
    overflow: hidden;
  }

  ${NavItem} {
    .primary-nav-label {
      font-weight: 600;
    }
  }

  ${LogoWrapper} {
    width: 100%;

    svg {
      width: 85%;
    }
  }

  ${PrimaryNavList} {
    flex-direction: column;
    align-items: flex-start;
    margin-right: 16px;
    margin-bottom: 36px;

    ul {
      position: relative;
      box-shadow: none;
      border-radius: 0;
    }

    > li:not(:last-child, :first-child) {
      border-bottom: 1px solid ${lightGray};
    }

    > li {
      width: 100%;
    }
  }
`

export const Header = styled.header<{mobileNavHeight: number}>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9000;

  min-height: 0;
  background-color: ${white};
  transition: background-color 0.4s;

  &.is-hamburger,
  &.is-transparent {
    background-color: transparent;
  }

  &.is-hamburger {
    &.is-open {
      height: 100vh;
      background-color: #00000040;
      overflow-y: auto;
    }
    &.is-nav-opening {
      min-height: 100vh;
      background-color: ${white};
    }
    &.is-nav-closing {
      background-color: ${white};
    }
    &.is-nav-opening.is-transparent,
    &.is-nav-opening.is-hamburger,
    &.is-nav-closing.is-transparent,
    &.is-nav-closing.is-hamburger {
      background-color: transparent;
    }
  }

  & > svg {
    min-width: ${LogoWidth};
  }
  a {
    text-decoration: none;
  }

  &.is-hamburger ${DesktopNavigationWrapper} {
    position: absolute;
    top: -999px;
    opacity: 0;
  }
  &.is-hamburger ${MobileNavigationWrapper} {
    position: initial;
    top: 0;
    opacity: 1;
  }

  &.is-nav-opening ${MobileNavigationWrapper} ${MainNav} {
    height: ${(p): number => p.mobileNavHeight ?? 0}px;
    opacity: 1;
    flex-grow: 1;
  }
  &.is-open ${MobileNavigationWrapper} ${MainNav} {
    height: auto;
    opacity: 1;
    flex-grow: 1;
  }
  &.is-nav-closing ${MobileNavigationWrapper} ${MainNav} {
    height: 0;
    opacity: 0;
  }
  &.is-open.is-nav-closing ${MobileNavigationWrapper} ${MainNav} {
    height: ${(p): number => p.mobileNavHeight ?? 0}px;
  }
`

export const RegionLocaleNavItemWrapper = styled.li`
  padding: 0 16px;
`
